// Mantine
import { Button, Anchor } from '@mantine/core'
import { modals } from '@mantine/modals'

// Lodash sortBy
import { orderBy } from 'lodash'

// Next Link
import { Link } from 'react-router-dom'

// Daysjs
import dayjs from 'dayjs'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// UI
import Box from '@/components/ui/Box'
import Badge from '@/components/ui/Badge'

const fulfillmentOrderStatus = {
    OPEN: {
        label: 'Demandée',
        badgeColor: 'orange',
    },
    CLOSED: {
        label: 'Expédiée',
        badgeColor: 'green',
    },
    CANCELLED: {
        label: 'Annulée',
        badgeColor: 'red',
    },
}

export default function RetailerEstimateFulfillmentOrders({
    estimate,
    reloadEstimate,
}) {
    const fulfillment_orders = orderBy(estimate.fulfillment_orders, 'id', 'asc')

    const existing_fulfillment_orders = fulfillment_orders.filter(
        (fulfillment_order) =>
            fulfillment_order.status === 'OPEN' ||
            fulfillment_order.status === 'CLOSED'
    )

    const canCreateNewFulfillmentOrder =
        existing_fulfillment_orders.length === 0

    // Cancel fulfillment order. The API action does more than flip the status:
    // it also clears the swap flag on a linked service case when no other
    // fulfillment order is left standing for it.
    const { requestPost } = useAxiosApi()

    const changeStatusToCancelled = async (fulfillment_order) => {
        await requestPost(
            `/fulfillment-orders/${fulfillment_order.id}/cancel`
        )
    }

    // Cancel fulfillment order (opens a confirmation modal)
    const cancelFulfillmentOrder = async (fulfillment_order) => {
        modals.openConfirmModal({
            title: 'Annuler la demande de préparation',
            children: (
                <div>
                    Êtes-vous sûr de vouloir annuler la demande de préparation
                    BP{fulfillment_order.id} ?
                </div>
            ),
            labels: {
                cancel: 'Annuler',
                confirm: 'Confirmer',
            },
            onConfirm: async () => {
                await changeStatusToCancelled(fulfillment_order)
                await reloadEstimate()
            },
        })
    }

    // Create fulfillment order
    const createFulfillmentOrder = async () => {
        await requestPost(
            `/retailers/retailer-estimates/${estimate.id}/create-fulfillment-order`
        )

        await reloadEstimate()
    }

    return (
        <Box title="Demandes de préparations">
            <div className="flex flex-col gap-3">
                {fulfillment_orders.map((fulfillment_order) => {
                    const statusText =
                        fulfillmentOrderStatus[fulfillment_order.status]
                            ?.label ?? 'Status inconnu'

                    const statusColor =
                        fulfillmentOrderStatus[fulfillment_order.status]
                            ?.badgeColor ?? 'gray'

                    return (
                        <Link
                            key={fulfillment_order.id}
                            className="grid grid-cols-[300px_1fr_1fr] py-2 items-center hover:bg-gray-50 border rounded-lg px-3"
                            to={`/fulfillment_orders/${fulfillment_order.id}`}
                        >
                            <div className="flex flex-col">
                                <Anchor component="span">
                                    BP{fulfillment_order.id}
                                </Anchor>

                                <span className="text-gray-400 text-sm">
                                    {dayjs(
                                        fulfillment_order.created_at
                                    ).calendar()}
                                </span>
                            </div>

                            <span className="text-gray-500 text-sm">
                                {fulfillment_order.location.name}
                            </span>

                            <div className="text-sm text-gray-800 flex justify-end items-center gap-3">
                                {fulfillment_order.status !== 'OPEN' && (
                                    <Badge color={statusColor} size="xs">
                                        {statusText}
                                    </Badge>
                                )}

                                {fulfillment_order.status === 'OPEN' && (
                                    <Button
                                        size="xs"
                                        color="red"
                                        variant="outline"
                                        onClick={() =>
                                            cancelFulfillmentOrder(
                                                fulfillment_order
                                            )
                                        }
                                    >
                                        Annuler la demande
                                    </Button>
                                )}
                            </div>
                        </Link>
                    )
                })}
            </div>

            {existing_fulfillment_orders.length === 0 && (
                <div className="flex justify-center text-gray-400 text-sm mb-10 mt-5">
                    <span>Pas de demande de préparation associée</span>
                </div>
            )}

            {canCreateNewFulfillmentOrder && (
                <div className="flex justify-center">
                    <Button onClick={createFulfillmentOrder}>
                        Créer une demande de préparation
                    </Button>
                </div>
            )}
        </Box>
    )
}
